import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Privacy = () => {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        const res = await fetch('/privacy.md', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load privacy policy (${res.status})`);
        const text = await res.text();
        setContent(text);
      } catch (e) {
        setError(e.message || 'Failed to load privacy policy.');
      }
    };
    fetchMarkdown();
  }, []);

  if (error) {
    return (
      <div className="text-white p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold mb-4">Privacy Policy</h1>
        <p className="text-red-300">{error}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="text-white p-6 max-w-3xl mx-auto">
        <p>Loading...</p>
      </div>
    );
  }

  // Custom renderers to style markdown elements without relying on Tailwind Typography
  const mdComponents = {
    h1: ({ node, ...props }) => (
      <h1 className="text-3xl font-extrabold mt-0 mb-4" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-2xl font-bold mt-8 mb-3" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-xl font-semibold mt-6 mb-2" {...props} />
    ),
    h4: ({ node, ...props }) => (
      <h4 className="text-lg font-semibold mt-5 mb-2" {...props} />
    ),
    h5: ({ node, ...props }) => (
      <h5 className="text-base font-semibold mt-4 mb-2" {...props} />
    ),
    h6: ({ node, ...props }) => (
      <h6 className="text-sm font-semibold mt-3 mb-1 uppercase tracking-wide" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className="mt-4 leading-7" {...props} />
    ),
    ul: ({ node, ordered, ...props }) => (
      <ul className="list-disc pl-6 space-y-2 mt-4" {...props} />
    ),
    ol: ({ node, ordered, ...props }) => (
      <ol className="list-decimal pl-6 space-y-2 mt-4" {...props} />
    ),
    li: ({ node, ...props }) => <li className="leading-7" {...props} />,
    a: ({ node, ...props }) => (
      <a className="underline" target="_blank" rel="noopener noreferrer" {...props} />
    ),
    hr: ({ node, ...props }) => (
      <hr className="my-6 border-black/20" {...props} />
    ),
    strong: ({ node, ...props }) => (
      <strong className="font-semibold" {...props} />
    ),
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm border-collapse border border-black/20" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className="bg-black/5" {...props} />
    ),
    th: ({ node, ...props }) => (
      <th className="border border-black/20 px-3 py-2 text-left font-semibold" {...props} />
    ),
    td: ({ node, ...props }) => (
      <td className="border border-black/20 px-3 py-2" {...props} />
    ),
  };

  return (
    <div className="max-w-3xl mx-auto p-4 mt-12 ">
      <article className="bg-white text-black rounded-xl shadow-lg p-6 md:p-8 md:mx-32 lg:mx-0">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{content}</ReactMarkdown>
      </article>
    </div>
  );
};

export default Privacy;
